// Composant universel : canvas de detail de carte (collection + duel)
import React, { useEffect, useCallback } from 'react';
import { formatEffectList, formatAbilityName, abilityDescription } from '../../utils/effectFormatter';
import './CardModal.css';

const RARITY_TEXT = {
  COMMON: 'Commune', NORMAL: 'Commune', RARE: 'Rare', EPIC: 'Épique', SUPER_RARE: 'Épique',
  SUPER_SUPER_RARE: 'Légendaire', LEGENDARY: 'Légendaire', RAINBOW: 'Arc-en-ciel'
};
const TYPE_TEXT = {
  CHARACTER: 'Personnage', SPELL: 'Sort', TRAP: 'Piège',
  EQUIPMENT: 'Équipement', TERRAIN: 'Terrain'
};
const SPELL_SPEED_ICON = { FAST: '⚡', SLOW: '🕒', FOCUS: '🔮', INSTANT: '✨' };

const CardModal = ({ card, onClose, footer }) => {
  const escClose = useCallback((e) => { if (e.key === 'Escape') onClose && onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', escClose);
    document.body.style.overflow = 'hidden';
    // Mesurer la hauteur reelle du header pour placer le panneau dessous
    const header = document.querySelector('.app-header');
    if (header) {
      document.documentElement.style.setProperty('--app-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
    }
    return () => {
      document.removeEventListener('keydown', escClose);
      document.body.style.overflow = '';
      document.documentElement.style.removeProperty('--app-header-h');
    };
  }, [escClose]);

  if (!card) return null;
  const el = (card.element || '').toLowerCase();

  return (
    <div className={`cm-overlay`} onClick={onClose}>
      <div
        className={`cm-panel element-${el} rarity-frame-${(card.rarity || '').toLowerCase()}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="cm-close" onClick={onClose} aria-label="Fermer">✕</button>

        <div className="cm-art-side">
          <div className="cm-art-frame">
            <img
              src={card.imageUrl || `/api/static/images/cards/${card.id}.webp`}
              alt={card.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `/api/static/images/card-backs/${el || 'neutral'}-back.svg`;
              }}
            />
            <span className={`cm-cost-crystal element-${el}`}>{card.cost}</span>
          </div>
          {card.type === 'CHARACTER' && (
            <div className="cm-stats-bar">
              <span className="cm-stat cm-atk">ATK <b>{card.attack ?? '-'}</b></span>
              <span className="cm-stat cm-def">PV <b>{card.defense ?? card.hp ?? '-'}</b></span>
            </div>
          )}
          {card.type === 'EQUIPMENT' && (
            <div className="cm-stats-bar">
              <span className="cm-stat cm-atk">ATK <b>+{card.attackBonus || 0}</b></span>
              <span className="cm-stat cm-def">PV <b>+{card.defenseBonus || 0}</b></span>
            </div>
          )}
        </div>

        <div className="cm-info-side">
          <div className="cm-head">
            <h2 className="cm-name">{card.name}</h2>
            <span className={`cm-rarity rarity-${(card.rarity || '').toLowerCase()}`}>
              {RARITY_TEXT[card.rarity] || card.rarity}
            </span>
          </div>

          <div className="cm-tags">
            <span className={`cm-element element-${el}`}>{card.element}</span>
            <span className="cm-type">{TYPE_TEXT[card.type] || card.type}</span>
            {card.spellSpeed && (
              <span className="cm-speed">{SPELL_SPEED_ICON[card.spellSpeed] || ''} {card.spellSpeed}</span>
            )}
          </div>

          <div className="cm-section">
            <h3 className="cm-section-title">Effet</h3>
            <p className="cm-effect">
{/* Effet reel de la carte : jamais le lore (description).
                  Combine : texte effect / effets structures / bonus equipement,
                  puis ajoute les capacites (abilities + keywords) si presentes. */
                (() => {
                  let text = null;
                  if (card.effect && typeof card.effect === 'string' && card.effect.trim()) {
                    text = card.effect;
                  } else if (Array.isArray(card.effects) && card.effects.length > 0) {
                    text = formatEffectList(card.effects);
                  } else if (card.type === 'EQUIPMENT' && ((card.attackBonus || 0) > 0 || (card.defenseBonus || 0) > 0)) {
                    text = 'Équipement : ' + [
                      (card.attackBonus || 0) > 0 ? '+' + card.attackBonus + ' ATK' : null,
                      (card.defenseBonus || 0) > 0 ? '+' + card.defenseBonus + ' PV' : null,
                    ].filter(Boolean).join(', ') + ' à la créature équipée.';
                  }
                  const kws = (card.abilities || []).concat(card.keywords || [])
                    .map(a => (typeof a === 'string' ? a : a.name || a.type))
                    .filter(Boolean);
                  const kwSpans = kws.map((raw, i) => {
                    const label = formatAbilityName(raw);
                    const desc = abilityDescription(raw);
                    return (
                      <span
                        key={raw + i}
                        className="cm-keyword"
                        title={desc || undefined}
                        style={{ borderBottom: desc ? '1px dotted #b9a7ff' : 'none', cursor: desc ? 'help' : 'default' }}
                      >
                        {label}
                      </span>
                    );
                  });
                  if (kwSpans.length > 0) {
                    text = text
                      ? <>{text} — Capacités : {kwSpans.reduce((acc, el, i) => [acc, i > 0 ? ', ' : null, el], [])}</>
                      : <>Capacités : {kwSpans.reduce((acc, el, i) => [acc, i > 0 ? ', ' : null, el], [])}</>;
                  }
                  return text || 'Aucun effet';
                })()
              }
            </p>
          </div>

          {(card.flavorText || card.description) && (
            <div className="cm-section">
              <h3 className="cm-section-title">Description</h3>
              <p className="cm-flavor">« {card.flavorText || card.description} »</p>
            </div>
          )}

          {footer && <div className="cm-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

export default CardModal;
